import { Controller } from "@hotwired/stimulus"

// Steuert die Anzeige des aktuellen Stream-Items.
// - Startet parallel einen Preload des nächsten Inhalts (via stream-preload Controller)
// - Wechselt nach content-type-specific duration zum vorgeladenen Inhalt
// - Ist nichts vorgeladen, wird ein weiterer Preload-Versuch gestartet
export default class extends Controller {
  static targets = ["image", "backgroundImage", "video", "comment"]
  static values = {
    photoDuration: { type: Number, default: 10000 },
    videoDuration: { type: Number, default: 10000 }
  }

  connect() {
    this.nextContent = null
    this.currentBlobUrl = null
    this.currentType = "image"
    this.dynamicPhotoDuration = null
    this.dynamicVideoDuration = null
    this.boundHandleReady = (event) => this.#handleReady(event)
    this.element.addEventListener("stream-preload:ready", this.boundHandleReady)

    // Initialen Video-Load starten falls vorhanden
    if (this.hasVideoTarget && this.videoTarget.src) {
      this.#setupInitialVideo()
    }

    // Preload wird vom stream-preload Controller automatisch in dessen connect() gestartet.
    this.#startInterval()
  }

  disconnect() {
    clearInterval(this.intervalId)
    this.element.removeEventListener("stream-preload:ready", this.boundHandleReady)
    this.#cleanupBlobUrl()
  }

  #startInterval() {
    const duration = this.currentType === "video"
      ? (this.dynamicVideoDuration || this.videoDurationValue)
      : (this.dynamicPhotoDuration || this.photoDurationValue)
    this.intervalId = setInterval(() => this.#advance(), duration)
  }

  #cleanupBlobUrl() {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl)
      this.currentBlobUrl = null
    }
  }

  #restartInterval() {
    clearInterval(this.intervalId)
    this.#startInterval()
  }

  #setupInitialVideo() {
    const video = this.videoTarget
    video.addEventListener("canplaythrough", () => {
      video.classList.remove("hidden")
    }, { once: true })
    video.addEventListener("play", () => {
      video.classList.remove("hidden")
    }, { once: true })
  }

  #handleReady(event) {
    const data = event.detail
    this.nextContent = data
    const previousPhotoDuration = this.dynamicPhotoDuration
    const previousVideoDuration = this.dynamicVideoDuration
    if (data.photo_duration) this.dynamicPhotoDuration = data.photo_duration * 1000
    if (data.video_duration) this.dynamicVideoDuration = data.video_duration * 1000
    const durationChanged =
      this.dynamicPhotoDuration !== previousPhotoDuration ||
      this.dynamicVideoDuration !== previousVideoDuration
    if (durationChanged) this.#restartInterval()
    if (data.immediate) this.#advance()
  }

  #advance() {
    this.#cleanupBlobUrl()
    if (this.nextContent) {
      this.#display(this.nextContent)
      this.nextContent = null
      this.#triggerPreload()
    } else {
      this.#triggerPreload()
    }
  }

  #display(content) {
    if (content.type === "image") {
      this.#displayImage(content)
    } else if (content.type === "video") {
      this.#displayVideo(content)
    }
    this.#updateComment(content)
  }

  #displayImage(content) {
    this.#cleanupBlobUrl()
    this.currentType = "image"
    this.#restartInterval()

    if (this.hasVideoTarget) {
      this.videoTarget.classList.add("hidden")
      this.videoTarget.pause?.()
      this.videoTarget.src = ""
    }

    const imageUrl = content.blob_url || content.url
    this.currentBlobUrl = content.blob_url || null

    if (this.hasImageTarget) {
      this.imageTarget.src = imageUrl
      this.imageTarget.classList.remove("hidden")
    }
    if (this.hasBackgroundImageTarget) {
      this.backgroundImageTarget.src = imageUrl
    }
  }

  #displayVideo(content) {
    this.#cleanupBlobUrl()
    this.currentType = "video"
    this.#restartInterval()

    if (this.hasImageTarget) {
      this.imageTarget.classList.add("hidden")
    }

    const videoUrl = content.blob_url || content.url
    this.currentBlobUrl = content.blob_url || null

    if (this.hasBackgroundImageTarget) {
      if (content.poster_url) {
        this.backgroundImageTarget.src = content.poster_url
        this.backgroundImageTarget.classList.remove("hidden")
      }
    }
    if (this.hasVideoTarget) {
      this.videoTarget.src = videoUrl
      if (content.poster_url) this.videoTarget.poster = content.poster_url
      this.videoTarget.classList.remove("hidden")
      this.videoTarget.play().catch(() => {})
    }
  }

  #updateComment(content) {
    if (!this.hasCommentTarget) return
    if (content.comment) {
      this.commentTarget.textContent = content.comment
      this.commentTarget.closest("[data-stream-comment-wrapper]")?.classList.remove("hidden")
    } else {
      this.commentTarget.closest("[data-stream-comment-wrapper]")?.classList.add("hidden")
    }
  }

  #triggerPreload() {
    const preload = this.application.getControllerForElementAndIdentifier(
      this.element, "stream-preload"
    )
    preload?.preload()
  }
}